import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import NumericTextField from "components/NumericTextField";
import SelectProfessionExtended, {
  ANY_PROFESSION_ID,
} from "components/SelectProfessionExtended";
import SelectStateMultiple from "components/SelectStateMultiple";
import SnackbarMessage from "components/SnackbarMessage";
import { QualificationIds } from "constants/QualificationIds";
import { FORM_ERROR } from "final-form";
import {
  ALL_APPROVED_ID,
  hasGroupLockedFields,
  MULTIPLE_VALUES_INDICATOR,
  processFormValues,
  SkuQualificationGroupModel,
} from "helpers/skuQualificationHelpers";
import { LegacyQualificationGroup } from "./legacy/grouping";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  DuplicateHandlingMode,
  GetMatchingQualificationApprovalRequest,
  ISkuQualificationRequest,
  SkuQualification,
  SkuQualificationPreviewResponse,
  SkuQualificationRequest,
  ValidateCourseNumberRequest,
  ValidateCourseNumberResponse,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import { ValidatePartialCredit } from "types/partialCreditValidator";
import { SkuFormProps } from "types/sku-related-types";
import { standardDateFormat, toUtcMidnight } from "utils/dateUtils";
import translateError from "utils/translateError";
import AcpeValidationDialog from "./AcpeValidationDialog";
import BulkSkuQualificationConfirmDialog from "./BulkSkuQualificationConfirmDialog";
import { InternalValidationDialog } from "./InternalValidationDialog";
import SelectQualification from "./SelectQualification";

const fakeProfessionIds = [ANY_PROFESSION_ID, ALL_APPROVED_ID];

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 0 20px",
  },
  expirationDateLabel: {
    fontSize: "11.5px",
  },
  dialogContent: { maxHeight: { xs: "70vh" } },
};

type SkuQualificationFormProps = {
  skuQualification?:
    | SkuQualification
    | SkuQualificationGroupModel
    | LegacyQualificationGroup
    | null;
  isChildEdit?: boolean;
  onClose: () => void;
  onCreate: () => void;
  onUpdate: () => void;
};

const SkuQualificationForm = ({
  sku,
  skuQualification,
  isChildEdit = false,
  onClose,
  onCreate,
  onUpdate,
  onSkuUpdated,
}: SkuQualificationFormProps & SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService, qualificationService } = useGlobalContext();
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [selectedQualificationId, setSelectedQualificationId] = useState<
    string | null
  >();
  const [selectedProfessionId, setSelectedProfessionId] = useState<
    string | undefined
  >();
  const [selectedStateIds, setSelectedStateIds] = useState<string[]>([]);
  const [formValues, setFormValues] = useState<ISkuQualificationRequest | null>(
    null,
  );
  const [
    previewResponse,
    setPreviewResponse,
  ] = useState<SkuQualificationPreviewResponse | null>(null);
  const [
    useQualificationApprovals,
    setUseQualificationApprovals,
  ] = useState<boolean>(false);
  const [confirmOpen, confirmOpenActions] = useBoolean(false);
  const [confirmLoading, confirmLoadingActions] = useBoolean(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [acpeValidationOpen, acpeValidationOpenActions] = useBoolean(false);
  const [internalValidationOpen, internalValidationOpenActions] = useBoolean(
    false,
  );
  const [
    internalValidationLoading,
    internalValidationLoadingActions,
  ] = useBoolean(false);
  const [
    internalValidationData,
    setInternalValidationData,
  ] = useState<ValidateCourseNumberResponse | null>(null);
  const [internalValidationError, setInternalValidationError] = useState<
    string | null
  >(null);
  const [
    pendingSubmitValues,
    setPendingSubmitValues,
  ] = useState<ISkuQualificationRequest | null>(null);
  const [pendingDuplicateHandling, setPendingDuplicateHandling] = useState<
    DuplicateHandlingMode | undefined
  >(undefined);

  const isGroupMode =
    skuQualification &&
    (skuQualification as SkuQualificationGroupModel).isGroup === true;
  const isCreateMode = !skuQualification;

  const isFieldsLocked = useMemo(
    () => (skuQualification ? hasGroupLockedFields(skuQualification) : false),
    [skuQualification],
  );

  const { organizationId } = contextStore.useState();

  const qualificationAcronym = skuQualification?.qualification?.acronym;
  const lockedFieldTooltip = isFieldsLocked
    ? t(
        { id: "skuAccreditationBodyFieldLockedTooltip" },
        { acronym: qualificationAcronym },
      )
    : "";

  const singleStateId =
    selectedStateIds.length === 1 ? selectedStateIds[0] : undefined;
  const isAllApprovedSelected = selectedStateIds.includes(ALL_APPROVED_ID);
  const allApprovedOptionSelected =
    selectedProfessionId === ALL_APPROVED_ID || isAllApprovedSelected;

  const init = useMemo(() => {
    const baseInit = {
      ...skuQualification,
      qualificationId: skuQualification?.qualification?.id ?? "",
      professionId: skuQualification?.profession?.id ?? "",
      stateIds: skuQualification?.stateId ? [skuQualification.stateId] : [],
    };

    if (isGroupMode) {
      const group = skuQualification as SkuQualificationGroupModel;
      const stateIds =
        group.isGroup && group.childQualifications
          ? group.childQualifications
              .map((child: SkuQualification) => child.stateId)
              .filter(Boolean)
          : [];

      const cleanValue = (val: any) =>
        val === MULTIPLE_VALUES_INDICATOR ? null : val;

      return {
        ...baseInit,
        stateIds,
        courseNumber: cleanValue(group.courseNumber),
        approvalCode: cleanValue(group.approvalCode),
        approvalDate: cleanValue(group.approvalDate),
        releaseDate: cleanValue(group.releaseDate),
        creditSubmissionExpirationDate: cleanValue(
          group.creditSubmissionExpirationDate,
        ),
        credits: cleanValue(group.credits),
        minimumCredit: cleanValue(group.minimumCredit),
        partialCreditIncrement: cleanValue(group.partialCreditIncrement),
        notes: cleanValue(group.notes),
      };
    }

    return baseInit;
  }, [skuQualification, isGroupMode]);

  const canAutoPopulatePartialCredits =
    !allApprovedOptionSelected && selectedStateIds.length <= 1;

  const { data: matchingQualificationApproval } = useQuery(
    [
      "matchingQualificationApproval",
      selectedQualificationId,
      selectedProfessionId,
      singleStateId,
      organizationId,
    ],
    () => {
      const stateParam = isAllApprovedSelected ? undefined : singleStateId;
      return qualificationService.getMatchingQualificationApproval(
        selectedQualificationId!,
        new GetMatchingQualificationApprovalRequest({
          professionId:
            selectedProfessionId &&
            fakeProfessionIds.includes(selectedProfessionId)
              ? undefined
              : selectedProfessionId,
          stateId: stateParam,
          organizationId,
        }),
      );
    },
    { enabled: !!selectedQualificationId && canAutoPopulatePartialCredits },
  );

  let formChange: ((name: any, value?: any) => void) | null = null;

  useEffect(() => {
    if (
      matchingQualificationApproval?.data &&
      formChange &&
      canAutoPopulatePartialCredits
    ) {
      if (
        skuQualification === null ||
        skuQualification?.minimumCredit === null
      ) {
        formChange(
          "minimumCredit",
          matchingQualificationApproval.data.minimumCredit,
        );
      }
      if (
        skuQualification === null ||
        skuQualification?.partialCreditIncrement === null
      ) {
        formChange(
          "partialCreditIncrement",
          matchingQualificationApproval.data.partialCreditIncrement,
        );
      }
    }

    if (formChange && allApprovedOptionSelected) {
      formChange("minimumCredit", null);
      formChange("partialCreditIncrement", null);
    }
  }, [
    formChange,
    matchingQualificationApproval?.data,
    skuQualification,
    canAutoPopulatePartialCredits,
    allApprovedOptionSelected,
  ]);

  const submit = async (
    values: ISkuQualificationRequest,
    duplicateHandling?: DuplicateHandlingMode,
  ) => {
    try {
      const {
        professionId,
        stateIds,
        useQualificationApprovals,
      } = processFormValues(values.professionId, values.stateIds);

      const id =
        isGroupMode ||
        useQualificationApprovals ||
        (stateIds && stateIds.length > 1)
          ? undefined
          : values.id;

      const childSkuQualificationIds =
        isGroupMode &&
        skuQualification &&
        "childQualifications" in skuQualification &&
        skuQualification.childQualifications
          ? (skuQualification.childQualifications
              .map((c: SkuQualification) => c.id)
              .filter(Boolean) as string[])
          : undefined;

      const requestPayload = {
        id,
        qualificationId: values.qualificationId,
        professionId,
        stateIds,
        useQualificationApprovals,
        duplicateHandling,
        courseNumber: values.courseNumber,
        approvalCode: values.approvalCode,
        approvalDate: toUtcMidnight(values.approvalDate),
        releaseDate: toUtcMidnight(values.releaseDate),
        creditSubmissionExpirationDate: toUtcMidnight(
          values.creditSubmissionExpirationDate,
        ),
        childSkuQualificationIds,
        credits: values.credits,
        minimumCredit: values.minimumCredit,
        partialCreditIncrement: values.partialCreditIncrement,
        notes: values.notes,
      };

      const response = await registerPromise(
        skuService.saveSkuQualification(
          sku.id!,
          SkuQualificationRequest.fromJS(requestPayload),
        ),
      );

      queryClient.invalidateQueries(["skuQualifications", sku.id!]);

      if (values.id === null) {
        onCreate();
      } else {
        onUpdate();
      }

      onSkuUpdated && onSkuUpdated(sku.id!);
      onClose();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const proceedAfterBulkPreview = async (
    values: ISkuQualificationRequest,
    duplicateHandling?: DuplicateHandlingMode,
  ) => {
    if (
      values.qualificationId === QualificationIds.Acpe &&
      values.courseNumber
    ) {
      setPendingDuplicateHandling(duplicateHandling);
      setFormValues(values);
      acpeValidationOpenActions.setTrue();
      return;
    }

    return await submit(values, duplicateHandling);
  };

  const proceedAfterInternalValidation = async (
    values: ISkuQualificationRequest,
  ) => {
    if (isCreateMode) {
      const {
        professionId,
        stateIds,
        useQualificationApprovals,
      } = processFormValues(values.professionId, values.stateIds);

      const previewPayload = {
        qualificationId: values.qualificationId,
        professionId,
        stateIds,
        useQualificationApprovals,
        releaseDate: toUtcMidnight(values.releaseDate),
        creditSubmissionExpirationDate: toUtcMidnight(
          values.creditSubmissionExpirationDate,
        ),
      } as SkuQualificationRequest;

      const preview = await registerPromise(
        skuService.previewBulkSaveSkuQualifications(sku.id!, previewPayload),
      );

      const hasExistingQualifications =
        (preview.data?.existingQualifications?.length ?? 0) > 0;

      if (useQualificationApprovals || hasExistingQualifications) {
        setPreviewResponse(preview.data ?? null);
        setUseQualificationApprovals(useQualificationApprovals);
        setFormValues(values);
        confirmOpenActions.setTrue();
        return;
      }
    }

    return await proceedAfterBulkPreview(values);
  };

  const onSubmit = async (values: ISkuQualificationRequest) => {
    try {
      if (
        !isGroupMode &&
        values.courseNumber &&
        values.courseNumber.trim() !== ""
      ) {
        const internalValidationRequest = new ValidateCourseNumberRequest({
          productId: sku.productId!,
          qualificationId: values.qualificationId,
          professionId: fakeProfessionIds.includes(values.professionId!)
            ? undefined
            : values.professionId,
          skuAvailabilityId: sku.skuAvailabilityId!,
          courseNumber: values.courseNumber,
          skuId: sku.id,
        });

        const internalValidationResponse = await qualificationService.validateCourseNumber(
          internalValidationRequest,
        );

        if (internalValidationResponse.data?.hasConflict) {
          setInternalValidationData(internalValidationResponse.data);
          setPendingSubmitValues(values);
          internalValidationOpenActions.setTrue();
          return;
        }
      }

      return await proceedAfterInternalValidation(values);
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onConfirm = async (duplicateHandling?: DuplicateHandlingMode) => {
    confirmLoadingActions.setTrue();

    try {
      const result = await proceedAfterBulkPreview(
        formValues!,
        duplicateHandling,
      );

      if (result && result[FORM_ERROR]) {
        setConfirmError(result[FORM_ERROR]);
        return;
      }

      confirmOpenActions.setFalse();
      setPreviewResponse(null);
      setUseQualificationApprovals(false);
      setConfirmError(null);
    } catch (err: any) {
      setConfirmError(translateError(err, t));
    } finally {
      confirmLoadingActions.setFalse();
    }
  };

  const onCancelConfirm = () => {
    if (!confirmLoading) {
      setFormValues(null);
      setPreviewResponse(null);
      setUseQualificationApprovals(false);
      setConfirmError(null);
      confirmOpenActions.setFalse();
    }
  };

  const onAcpeSave = async (
    releaseDate?: Date,
    expirationDate?: Date,
    contactHours?: number,
  ) => {
    if (!formValues) return;

    const values = { ...formValues };
    if (releaseDate) {
      values.releaseDate = releaseDate;
    }
    if (expirationDate) {
      values.creditSubmissionExpirationDate = expirationDate;
    }
    if (contactHours !== undefined && contactHours !== null) {
      values.credits = contactHours;
    }

    const result = await submit(values, pendingDuplicateHandling);
    setPendingDuplicateHandling(undefined);
    return result;
  };

  const onAcpeClose = () => {
    acpeValidationOpenActions.setFalse();
    setFormValues(null);
    setPendingDuplicateHandling(undefined);
  };

  const onInternalValidationProceed = async () => {
    internalValidationLoadingActions.setTrue();

    try {
      const result = await proceedAfterInternalValidation(pendingSubmitValues!);

      if (result && result[FORM_ERROR]) {
        setInternalValidationError(result[FORM_ERROR]);
        return;
      }

      internalValidationOpenActions.setFalse();
      setPendingSubmitValues(null);
      setInternalValidationData(null);
      setInternalValidationError(null);
    } catch (err: any) {
      setInternalValidationError(translateError(err, t));
    } finally {
      internalValidationLoadingActions.setFalse();
    }
  };

  const onInternalValidationCancel = () => {
    internalValidationOpenActions.setFalse();
    setPendingSubmitValues(null);
    setInternalValidationData(null);
    setInternalValidationError(null);
  };

  const validateForm = (values: ISkuQualificationRequest) => {
    const errors = ValidatePartialCredit(t, values);

    if (
      values.credits &&
      values.minimumCredit &&
      values.credits < values.minimumCredit
    ) {
      errors.credits = t({ id: "cantBeLessThanMinimumCredits" });
    }

    if (
      values.releaseDate &&
      values.creditSubmissionExpirationDate &&
      values.releaseDate > values.creditSubmissionExpirationDate
    ) {
      errors.creditSubmissionExpirationDate = t({
        id: "creditSubmissionExpirationDateCannotBeLessThanReleaseDate",
      });
    }

    return errors;
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      validate={validateForm}
      render={({
        handleSubmit,
        submitting,
        submitError,
        form: { change },
        values,
      }) => {
        formChange = change;

        return (
          <form onSubmit={handleSubmit} noValidate>
            <DialogTitle id="form-dialog-title">
              <T id="skuAccreditationBodyFormTitle" />
            </DialogTitle>
            <DialogContent dividers sx={styles.dialogContent}>
              <Grid container spacing={2}>
                <Field name="qualificationId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12} md={6}>
                      <Tooltip title={lockedFieldTooltip} placement="top">
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectQualification
                            required
                            disabled={!isCreateMode}
                            organizationId={sku.ownerOrganizationId!}
                            label={t({
                              id: "accreditationBodyLabel",
                            })}
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            value={input.value}
                            onChange={(qual) => {
                              const id = qual?.id ?? null;
                              setSelectedQualificationId(id);
                              input.onChange(id);
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>

                <Field name="professionId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12} md={6}>
                      <Tooltip title={lockedFieldTooltip} placement="top">
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProfessionExtended
                            {...input}
                            required
                            disabled={!isCreateMode}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            anyProfessionAllowed={!allApprovedOptionSelected}
                            onChange={(id) => {
                              setSelectedProfessionId(id);
                              input.onChange(id);
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>

                <Field name="stateIds" required={allApprovedOptionSelected}>
                  {({ input, meta }) => (
                    <Grid item xs={12} md={6}>
                      <Tooltip title={lockedFieldTooltip} placement="top">
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectStateMultiple
                            value={input.value || []}
                            onChange={(ids) => {
                              setSelectedStateIds(ids);
                              input.onChange(ids);
                            }}
                            disabled={!isCreateMode}
                            required={allApprovedOptionSelected}
                            label={t({ id: "locationFormStateLabel" })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>

                <Field name="courseNumber">
                  {({ input, meta }) => (
                    <Grid item xs={12} md={6}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({
                          id: "courseNumber",
                        })}
                        inputProps={{
                          maxLength: 250,
                        }}
                        InputLabelProps={{ required: false }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        value={input.value.trim()}
                      />
                    </Grid>
                  )}
                </Field>

                <Grid item xs={6} md={3}>
                  <Field name="approvalCode">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({
                            id: "approvalCode",
                          })}
                          InputLabelProps={{ required: false }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={6} md={3}>
                  <Field name="approvalDate" isDate>
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <DatePicker
                          label={t({
                            id: "approvalDate",
                          })}
                          format={standardDateFormat}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              error: meta.error && meta.submitFailed,
                              helperText:
                                meta.error && meta.submitFailed && meta.error,
                            },
                            actionBar: {
                              actions: ["clear", "cancel"],
                            },
                          }}
                          {...input}
                          value={input.value === "" ? null : input.value}
                        />
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Field name="releaseDate" isDate>
                  {({ input, meta }) => (
                    <Grid item xs={6} md={3}>
                      <Tooltip title={lockedFieldTooltip} placement="top">
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "skuFormReleaseDateLabel" })}
                            format={standardDateFormat}
                            disabled={isFieldsLocked}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>

                <Field name="creditSubmissionExpirationDate" isDate>
                  {({ input, meta }) => (
                    <Grid item xs={6} md={3}>
                      <Tooltip title={lockedFieldTooltip} placement="top">
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={
                              <Box
                                component="span"
                                sx={styles.expirationDateLabel}
                              >
                                {t({
                                  id:
                                    "skuFormCreditSubmissionExpirationDateLabel",
                                })}
                              </Box>
                            }
                            format={standardDateFormat}
                            disabled={isFieldsLocked}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>

                <Field name="credits" type="number">
                  {({ input, meta }) => (
                    <Grid item xs={12} md={4}>
                      <Tooltip title={lockedFieldTooltip} placement="top">
                        <Box>
                          <NumericTextField
                            variant="standard"
                            fullWidth
                            disabled={isFieldsLocked}
                            label={t({ id: "credits" })}
                            error={meta.error && meta.submitFailed}
                            helperText={meta.error && meta.submitFailed}
                            {...input}
                          />
                        </Box>
                      </Tooltip>
                      {meta.error && meta.submitFailed && (
                        <FormHelperText error>{meta.error}</FormHelperText>
                      )}
                    </Grid>
                  )}
                </Field>

                <Field name="minimumCredit" type="number">
                  {({ input, meta }) => (
                    <Grid item xs={12} md={4}>
                      <NumericTextField
                        variant="standard"
                        disabled={allApprovedOptionSelected}
                        fullWidth
                        label={t({ id: "minimumCreditMessage" })}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed}
                        {...input}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText error>{meta.error}</FormHelperText>
                      )}
                    </Grid>
                  )}
                </Field>

                <Field name="partialCreditIncrement" type="number">
                  {({ input, meta }) => (
                    <Grid item xs={12} md={4}>
                      <NumericTextField
                        variant="standard"
                        disabled={allApprovedOptionSelected}
                        fullWidth
                        label={t({ id: "partialCreditIncrementMessage" })}
                        error={meta.error && meta.submitFailed}
                        helperText={meta.error && meta.submitFailed}
                        {...input}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText error>{meta.error}</FormHelperText>
                      )}
                    </Grid>
                  )}
                </Field>

                <Field name="notes">
                  {({ input, meta }) => (
                    <Grid item xs={12} mt={2}>
                      <FieldRichText
                        label={t({ id: "notes" })}
                        meta={meta}
                        input={input}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={submitting} onClick={onClose}>
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: "skuAccreditationBodySaveButtonLabel" })}
              </Button>
            </DialogActions>
            <BulkSkuQualificationConfirmDialog
              open={confirmOpen}
              loading={confirmLoading}
              qualificationId={values.qualificationId}
              useQualificationApprovals={useQualificationApprovals}
              previewResponse={previewResponse}
              onConfirm={onConfirm}
              onCancel={onCancelConfirm}
              error={confirmError}
            />
            {internalValidationData && (
              <InternalValidationDialog
                open={internalValidationOpen}
                productName={sku.title || "this Product"}
                courseNumberBeingCreated={
                  pendingSubmitValues?.courseNumber || ""
                }
                validationData={internalValidationData}
                loading={internalValidationLoading}
                error={internalValidationError}
                onProceed={onInternalValidationProceed}
                onCancel={onInternalValidationCancel}
              />
            )}
            <AcpeValidationDialog
              open={acpeValidationOpen}
              skuQualificationRequest={formValues}
              onClose={onAcpeClose}
              onSave={onAcpeSave}
            />
            <SnackbarMessage
              snackbarMessage={snackbarMessage}
              setSnackbarMessage={setSnackbarMessage}
            />
          </form>
        );
      }}
    />
  );
};

export default SkuQualificationForm;
